import { useEffect, useMemo, useState } from 'react';
import moment from 'moment';

const TICK_INTERVAL_MS = 1000;

/**
 * Whole seconds since `startedAt`, re-read from the clock every second while `isRunning`.
 *
 * The interval only exists while there is something to count: it is torn down when the run stops,
 * when the start time changes, and on unmount. Returns null with no start time, and holds its last
 * value once stopped — at which point the caller should switch to a span measured between the run's
 * own events, which is immune to the clock caveats below.
 */
export const useElapsedSeconds = (startedAt: string | undefined, isRunning: boolean): number | null => {
  // Parsed as UTC because the events carry a UTC ISO string; a bare `new Date` would read one without
  // an offset as local time and be out by the whole timezone. Parsed once per start time, not per tick.
  const startedAtMs = useMemo(() => (startedAt ? moment.utc(startedAt).valueOf() : null), [startedAt]);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (startedAtMs === null || !isRunning) {
      return undefined;
    }

    // Re-read on subscribe so the first frame is accurate rather than however stale mount-time was.
    setNow(Date.now());
    const interval = setInterval(() => setNow(Date.now()), TICK_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [startedAtMs, isRunning]);

  if (startedAtMs === null) {
    return null;
  }

  // The start time is stamped by a worker and compared against the browser's clock, so the two can
  // disagree. Floored at zero, so a worker running ahead reads as 0s and starts counting once the
  // browser catches up, rather than counting backwards.
  return Math.max(0, Math.floor((now - startedAtMs) / 1000));
};

export default useElapsedSeconds;
